/**
 * ConflictVisualizationPanel Component
 * 
 * Task 8.1: Create ConflictVisualizationPanel component
 * Requirements: 11.6, 11.7, 11.8
 * 
 * Collapsible panel showing all conflicts with navigation and resolution options.
 * Groups conflicts by type (teacher, room, class) and provides "Go to" functionality.
 */

import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Badge,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Collapse,
  Divider,
  Button,
  styled,
} from '@mui/material';
import {
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Room as RoomIcon,
  Class as ClassIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import type { TimetableRecordWithRelations } from '@timetable/types';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

export interface Conflict {
  id: string;
  type: 'teacher' | 'room' | 'class';
  severity: 'hard' | 'soft';
  message: string;
  affectedRecords: TimetableRecordWithRelations[];
  suggestedResolutions?: Resolution[];
}

export interface Resolution {
  type: 'reassign_teacher' | 'reassign_room' | 'move_period' | 'remove_period';
  description: string;
  action: () => void;
}

export interface ConflictVisualizationPanelProps {
  conflicts: Conflict[];
  open: boolean;
  onClose: () => void;
  onConflictClick: (conflict: Conflict) => void;
  onResolve?: (conflictId: string, resolution: Resolution) => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '400px',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
}));

const ConflictHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const ConflictTypeSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const ConflictTypeHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ConflictItem = styled(ListItem)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.error.main}`,
  backgroundColor: theme.palette.background.paper,
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// ============================================================================
// Helper Functions
// ============================================================================

function getConflictIcon(type: Conflict['type']) {
  switch (type) {
    case 'teacher':
      return <PersonIcon />;
    case 'room':
      return <RoomIcon />;
    case 'class':
      return <ClassIcon />;
    default:
      return <WarningIcon />;
  }
}

function getConflictColor(severity: Conflict['severity']) {
  return severity === 'hard' ? 'error' : 'warning';
}

function getConflictTypeLabel(type: Conflict['type']) {
  switch (type) {
    case 'teacher':
      return 'Teacher Conflicts';
    case 'room':
      return 'Room Conflicts';
    case 'class':
      return 'Class Conflicts';
    default:
      return 'Conflicts';
  }
}

// ============================================================================
// Main Component
// ============================================================================

export function ConflictVisualizationPanel({
  conflicts,
  open,
  onClose,
  onConflictClick,
  onResolve,
}: ConflictVisualizationPanelProps) {
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(
    new Set(['teacher', 'room', 'class'])
  );

  // Group conflicts by type
  const conflictsByType = conflicts.reduce((acc, conflict) => {
    if (!acc[conflict.type]) {
      acc[conflict.type] = [];
    }
    acc[conflict.type].push(conflict);
    return acc;
  }, {} as Record<string, Conflict[]>);

  const toggleTypeExpansion = (type: string) => {
    setExpandedTypes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(type)) {
        newSet.delete(type);
      } else {
        newSet.add(type);
      }
      return newSet;
    });
  };

  const handleConflictClick = (conflict: Conflict) => {
    onConflictClick(conflict);
  };

  const totalConflicts = conflicts.length;
  const hardConflicts = conflicts.filter((c) => c.severity === 'hard').length;
  const softConflicts = conflicts.filter((c) => c.severity === 'soft').length;

  return (
    <StyledDrawer anchor="right" open={open} onClose={onClose}>
      {/* Header */}
      <ConflictHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge badgeContent={totalConflicts} color="error">
            <WarningIcon color="error" />
          </Badge>
          <Typography variant="h6" fontWeight={600}>
            Conflicts
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </ConflictHeader>

      {/* Summary */}
      {totalConflicts > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Chip
            label={`${hardConflicts} Hard`}
            color="error"
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${softConflicts} Soft`}
            color="warning"
            size="small"
            variant="outlined"
          />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Empty State */}
      {totalConflicts === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            color: 'text.secondary',
          }}
        >
          <WarningIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
          <Typography variant="body2">No conflicts detected</Typography>
          <Typography variant="caption" color="text.disabled">
            All periods are properly scheduled
          </Typography>
        </Box>
      )}

      {/* Conflict Groups */}
      {Object.entries(conflictsByType).map(([type, typeConflicts]) => (
        <ConflictTypeSection key={type}>
          <ConflictTypeHeader onClick={() => toggleTypeExpansion(type)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getConflictIcon(type as Conflict['type'])}
              <Typography variant="subtitle2" fontWeight={600}>
                {getConflictTypeLabel(type as Conflict['type'])}
              </Typography>
              <Chip label={typeConflicts.length} size="small" color="error" />
            </Box>
            {expandedTypes.has(type) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ConflictTypeHeader>

          <Collapse in={expandedTypes.has(type)}>
            <List sx={{ mt: 1 }}>
              {typeConflicts.map((conflict) => (
                <ConflictItem key={conflict.id} disablePadding>
                  <ListItemButton onClick={() => handleConflictClick(conflict)}>
                    <ListItemIcon>
                      <WarningIcon
                        color={getConflictColor(conflict.severity)}
                        fontSize="small"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {conflict.message}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {conflict.affectedRecords.length} period(s) affected
                          </Typography>
                          {conflict.severity === 'hard' && (
                            <Chip
                              label="Must Fix"
                              size="small"
                              color="error"
                              sx={{ ml: 1, height: 18 }}
                            />
                          )}
                        </Box>
                      }
                    />
                    <NavigateNextIcon fontSize="small" color="action" />
                  </ListItemButton>

                  {/* Suggested Resolutions */}
                  {conflict.suggestedResolutions &&
                    conflict.suggestedResolutions.length > 0 && (
                      <Box sx={{ px: 2, pb: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          Suggested resolutions:
                        </Typography>
                        {conflict.suggestedResolutions.map((resolution, idx) => (
                          <Button
                            key={idx}
                            size="small"
                            variant="outlined"
                            fullWidth
                            sx={{ mb: 0.5, justifyContent: 'flex-start' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (onResolve) {
                                onResolve(conflict.id, resolution);
                              }
                            }}
                          >
                            {resolution.description}
                          </Button>
                        ))}
                      </Box>
                    )}
                </ConflictItem>
              ))}
            </List>
          </Collapse>
        </ConflictTypeSection>
      ))}
    </StyledDrawer>
  );
}
